import axios from "axios";
import toast from "react-hot-toast";
import uPlot, { Series } from "uplot";
import React, { useEffect, useMemo, useState } from "react";

import Loader from "../../Loader/Loader";
import FilterComponent from "./FilterComponent";
import LineChartUplot from "../../Global Components/LineChartUplot";
import { APP_BASE_URL, GET_TELEMETRY_HISTORY_BY_TIMESTAMP_WITH_KEYS } from "../../../Common/EndPoints";
import {
  getFilteredPointOperationData,
  getParsedPointOperationChartData,
  uPlotTooltipPlugin,
} from "./PointOperationChartUtils";

const default_state = { data: [], series: [] };

async function getPointOperationData(filterParams: any, deviceId: string) {
  const keys = ["EVENTTIME", "EVENTDATA1"];
  if (filterParams.key) {
    keys.push(filterParams.key);
  }
  if (filterParams.direction) {
    keys.push("OperationType");
  }
  const params = {
    deviceId: deviceId,
    startTs: filterParams.startTs,
    endTs: filterParams.endTs,
    keys: keys.toString(),
  };
  const url = `${APP_BASE_URL}${GET_TELEMETRY_HISTORY_BY_TIMESTAMP_WITH_KEYS}`;
  try {
    const response = await axios.get(url, { params });
    const data = response.data.data;
    if ((filterParams.key && filterParams.key in data) || filterParams.direction) {
      return getParsedPointOperationChartData(getFilteredPointOperationData(data, filterParams));
    }
    return getParsedPointOperationChartData(data);
  } catch (error: any) {
    if (error?.response?.data?.error) {
      throw new Error(error?.response?.data?.error);
    }
    throw error;
  }
}

interface Props {
  initial_data?: { data: uPlot.AlignedData; series: Series[] };
  disableFilter?: boolean;
  deviceId: string;
  deviceName: string;
}

function getSize() {
  return {
    width: window.innerWidth - 100,
    height: window.innerHeight - 200,
  };
}

const PointOperationChartNew: React.FC<Props> = ({
  initial_data = default_state,
  disableFilter = false,
  deviceId,
  deviceName,
}) => {
  const [state, setState] = useState(initial_data);
  const [loader, setLoader] = useState(false);
  const [size, setSize] = useState(getSize());
  const station_name = deviceName?.split("-")?.[0];

  const options = useMemo<uPlot.Options>(
    () => ({
      title: deviceName + " Operation Chart",
      ...size,
      focus: {
        alpha: 1,
      },
      cursor: {
        focus: {
          prox: 5,
        },
        drag: {
          x: true,
          y: true,
          uni: 50,
        },
      },
      series: [
        {
          label: "Time(mS)",
        },
        ...state.series,
      ],
      plugins: [uPlotTooltipPlugin({})],
      scales: { x: { time: false } },
      legend: {
        show: false,
      },
    }),
    [deviceName, state.series, size],
  );

  function handleChartSize() {
    setSize(getSize());
  }

  useEffect(() => {
    window.addEventListener("resize", handleChartSize);

    return () => window.removeEventListener("resize", handleChartSize);
  }, []);

  async function handleFilterSearch(filterParams: any) {
    try {
      setLoader(true);
      const response = await getPointOperationData(filterParams, deviceId);
      setState(response);
      setLoader(false);
    } catch (error: any) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Something went wrong");
      }
      setState(default_state);
    }
  }

  return (
    <div>
      {loader && <Loader />}
      <div className="header">
        <div className="annotation">
          <div>X-Axis: Time(mS)</div>
          <div>Y-Axis: Current(mA)</div>
        </div>
        {!disableFilter && (
          <div className="filter">
            <FilterComponent
              getFilterParams={handleFilterSearch}
              resetFilters={() => setState(initial_data)}
              show_direction_filter={station_name === "VS"}
            />
          </div>
        )}
      </div>
      <LineChartUplot data={state.data} options={options} />
    </div>
  );
};

export default PointOperationChartNew;
